import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkDeleteQuestionEntriesResponse,
  BulkUpdateQuestionEntryOptions,
  BulkUpdateQuestionEntryResponse,
  ListQuestionEntriesOptions,
  ListQuestionEntriesResponse,
  QueryQuestionEntriesOptions,
  QueryQuestionEntriesResponse,
  QuestionEntriesQueryBuilder,
  QuestionEntry,
  QuestionEntryCreatedEnvelope,
  QuestionEntryDeletedEnvelope,
  QuestionEntryQuery,
  QuestionEntryUpdatedEnvelope,
  SetQuestionEntryLabelsOptions,
  SetQuestionEntryLabelsResponse,
  UpdateExtendedFieldsOptions,
  UpdateExtendedFieldsResponse,
  UpdateQuestionEntry,
  bulkDeleteQuestionEntries as universalBulkDeleteQuestionEntries,
  bulkUpdateQuestionEntry as universalBulkUpdateQuestionEntry,
  createQuestionEntry as universalCreateQuestionEntry,
  deleteQuestionEntry as universalDeleteQuestionEntry,
  getQuestionEntry as universalGetQuestionEntry,
  listQuestionEntries as universalListQuestionEntries,
  queryQuestionEntries as universalQueryQuestionEntries,
  setQuestionEntryLabels as universalSetQuestionEntryLabels,
  typedQueryQuestionEntries as universalTypedQueryQuestionEntries,
  updateExtendedFields as universalUpdateExtendedFields,
  updateQuestionEntry as universalUpdateQuestionEntry,
} from './faq-question-entry-v2-question-entry-question-entry.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/faq' };

export function createQuestionEntry(
  httpClient: HttpClient
): CreateQuestionEntrySignature {
  return (questionEntry: NonNullablePaths<QuestionEntry, `question`, 0>) =>
    universalCreateQuestionEntry(
      questionEntry,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateQuestionEntrySignature {
  /**
   * Creates a question entry with the provided details including question text, answer content, and category assignment.
   * The question entry must be assigned to an existing category and can optionally include labels for additional organization.
   * @param - Question entry to create.
   * @returns Created question entry.
   */
  (questionEntry: NonNullablePaths<QuestionEntry, `question`, 0>): Promise<
    NonNullablePaths<
      QuestionEntry,
      `draftjs` | `plainText` | `labels` | `labels.${number}.title`,
      2
    >
  >;
}

export function getQuestionEntry(
  httpClient: HttpClient
): GetQuestionEntrySignature {
  return (questionEntryId: string) =>
    universalGetQuestionEntry(
      questionEntryId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetQuestionEntrySignature {
  /**
   * Retrieves a single question entry by ID.
   * @param - ID of the question entry to retrieve.
   * @returns Retrieved question entry.
   */
  (questionEntryId: string): Promise<
    NonNullablePaths<
      QuestionEntry,
      `draftjs` | `plainText` | `labels` | `labels.${number}.title`,
      2
    >
  >;
}

export function updateQuestionEntry(
  httpClient: HttpClient
): UpdateQuestionEntrySignature {
  return (
    _id: string,
    questionEntry: NonNullablePaths<UpdateQuestionEntry, `revision`, 0>
  ) =>
    universalUpdateQuestionEntry(
      _id,
      questionEntry,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateQuestionEntrySignature {
  /**
   * Updates a question entry.
   *
   * Each time the question entry is updated, `revision` increments by 1.
   * The current `revision` must be specified when updating the question entry.
   * This ensures you're working with the latest version of a question entry and prevents unintended overwrites.
   *
   * To update multiple question entries, call the Bulk Update Question Entry method.
   *
   * To update only the question entry's `extendedFields` field, call the Update Extended Fields method.
   *
   * To update only the question entry's labels, call the Set Question Entry Labels method.
   * @param - Question entry ID.
   * @returns Updated question entry.
   */
  (
    _id: string,
    questionEntry: NonNullablePaths<UpdateQuestionEntry, `revision`, 0>
  ): Promise<
    NonNullablePaths<
      QuestionEntry,
      `draftjs` | `plainText` | `labels` | `labels.${number}.title`,
      2
    >
  >;
}

export function deleteQuestionEntry(
  httpClient: HttpClient
): DeleteQuestionEntrySignature {
  return (questionEntryId: string) =>
    universalDeleteQuestionEntry(
      questionEntryId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteQuestionEntrySignature {
  /**
   * Deletes a question entry.
   *
   * Deleting a question entry permanently removes it from the site's Wix FAQ dashboard page.
   *
   * To delete multiple question entries, call the Bulk Delete Question Entries method.
   * @param - ID of the question entry to delete.
   */
  (questionEntryId: string): Promise<void>;
}

export function listQuestionEntries(
  httpClient: HttpClient
): ListQuestionEntriesSignature {
  return (options?: ListQuestionEntriesOptions) =>
    universalListQuestionEntries(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListQuestionEntriesSignature {
  /**
   * Retrieves a list of question entries, given the specified filters and paging.
   *
   * See [Query Question Entries](https://dev.wix.com/docs/rest/business-management/faq-app/faq/question-entry-v2/query-question-entries) for a list of supported filters.
   *
   * Use the `fieldSet` parameter to control whether sharing links are included in the response.
   */
  (options?: ListQuestionEntriesOptions): Promise<
    NonNullablePaths<
      ListQuestionEntriesResponse,
      | `questionEntries`
      | `questionEntries.${number}.draftjs`
      | `questionEntries.${number}.plainText`,
      2
    >
  >;
}

export function queryQuestionEntries(
  httpClient: HttpClient
): QueryQuestionEntriesSignature {
  return (options?: QueryQuestionEntriesOptions) =>
    universalQueryQuestionEntries(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryQuestionEntriesSignature {
  /**
   * Retrieves question entries, given the provided paging, filtering, and sorting.
   */
  (options?: QueryQuestionEntriesOptions): QuestionEntriesQueryBuilder;
}

export function typedQueryQuestionEntries(
  httpClient: HttpClient
): TypedQueryQuestionEntriesSignature {
  return (query: QuestionEntryQuery, options?: QueryQuestionEntriesOptions) =>
    universalTypedQueryQuestionEntries(
      query,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryQuestionEntriesSignature {
  /** */
  (query: QuestionEntryQuery, options?: QueryQuestionEntriesOptions): Promise<
    NonNullablePaths<
      QueryQuestionEntriesResponse,
      | `questionEntries`
      | `questionEntries.${number}.draftjs`
      | `questionEntries.${number}.plainText`,
      2
    >
  >;
}

export function bulkDeleteQuestionEntries(
  httpClient: HttpClient
): BulkDeleteQuestionEntriesSignature {
  return (questionEntryIds: string[]) =>
    universalBulkDeleteQuestionEntries(
      questionEntryIds,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDeleteQuestionEntriesSignature {
  /**
   * Deletes multiple question entries.
   *
   * Deleting a question entry permanently removes it from the site's Wix FAQ dashboard page.
   *
   * To delete a single question entry, call the Delete Question Entry method.
   * @param - IDs of question entries to delete.
   */
  (questionEntryIds: string[]): Promise<
    NonNullablePaths<
      BulkDeleteQuestionEntriesResponse,
      | `itemMetadata`
      | `itemMetadata.${number}.originalIndex`
      | `itemMetadata.${number}.success`
      | `itemMetadata.${number}.error.code`
      | `itemMetadata.${number}.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      3
    >
  >;
}

export function updateExtendedFields(
  httpClient: HttpClient
): UpdateExtendedFieldsSignature {
  return (
    _id: string,
    namespace: string,
    options: NonNullablePaths<UpdateExtendedFieldsOptions, `namespaceData`, 0>
  ) =>
    universalUpdateExtendedFields(
      _id,
      namespace,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateExtendedFieldsSignature {
  /**
   * Updates extended fields of a question entry without incrementing `revision`.
   * @param - ID of the entity to update.
   * @param - Identifier for the app whose extended fields are being updated.
   */
  (
    _id: string,
    namespace: string,
    options: NonNullablePaths<UpdateExtendedFieldsOptions, `namespaceData`, 0>
  ): Promise<
    NonNullablePaths<
      UpdateExtendedFieldsResponse,
      | `questionEntry.draftjs`
      | `questionEntry.plainText`
      | `questionEntry.labels`
      | `questionEntry.labels.${number}.title`,
      3
    >
  >;
}

export function setQuestionEntryLabels(
  httpClient: HttpClient
): SetQuestionEntryLabelsSignature {
  return (questionEntryId: string, options?: SetQuestionEntryLabelsOptions) =>
    universalSetQuestionEntryLabels(
      questionEntryId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface SetQuestionEntryLabelsSignature {
  /**
   * Sets `labels` for a question entry.
   *
   * This method replaces all existing labels for the question entry.
   * To clear all labels, specify an empty labels array.
   * @param - ID of the question entry to update labels for.
   */
  (questionEntryId: string, options?: SetQuestionEntryLabelsOptions): Promise<
    NonNullablePaths<
      SetQuestionEntryLabelsResponse,
      | `questionEntry.draftjs`
      | `questionEntry.plainText`
      | `questionEntry.labels`
      | `questionEntry.labels.${number}.title`,
      3
    >
  >;
}

export function bulkUpdateQuestionEntry(
  httpClient: HttpClient
): BulkUpdateQuestionEntrySignature {
  return (
    options?: NonNullablePaths<
      BulkUpdateQuestionEntryOptions,
      | `questionEntries.${number}.questionEntry`
      | `questionEntries.${number}.questionEntry._id`
      | `questionEntries.${number}.questionEntry.revision`,
      3
    >
  ) =>
    universalBulkUpdateQuestionEntry(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateQuestionEntrySignature {
  /**
   * Updates multiple question entries.
   *
   * Each time a question entry is updated, its `revision` increments by 1.
   * The current `revision` must be specified when updating the question entry.
   * This ensures you're working with the latest version of a question entry and prevents unintended overwrites.
   *
   * To update a single question entry, call the Update Question Entry method.
   */
  (
    options?: NonNullablePaths<
      BulkUpdateQuestionEntryOptions,
      | `questionEntries.${number}.questionEntry`
      | `questionEntries.${number}.questionEntry._id`
      | `questionEntries.${number}.questionEntry.revision`,
      3
    >
  ): Promise<
    NonNullablePaths<
      BulkUpdateQuestionEntryResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.questionEntry.draftjs`
      | `results.${number}.questionEntry.plainText`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      4
    >
  >;
}

export const onQuestionEntryCreated = /*#__PURE__*/ EventDefinition(
  'wix.faq.question_entry.v2.question_entry_created',
  true,
  (event: QuestionEntryCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.richContent.metadata.createdTimestamp' },
            { path: 'entity.richContent.metadata.updatedTimestamp' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'entity.sortOrder' },
            {
              path: 'entity.richContent.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.richContent.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.richContent.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.richContent.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.richContent.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.richContent.nodes.galleryData.items.video.thumbnail.duration',
            },
            { path: 'entity.richContent.nodes.galleryData.options.item.ratio' },
            { path: 'entity.richContent.nodes.imageData.image.duration' },
            { path: 'entity.richContent.nodes.imageData.crop.x' },
            { path: 'entity.richContent.nodes.imageData.crop.y' },
            { path: 'entity.richContent.nodes.imageData.crop.width' },
            { path: 'entity.richContent.nodes.imageData.crop.height' },
            { path: 'entity.richContent.nodes.mapData.mapSettings.lat' },
            { path: 'entity.richContent.nodes.mapData.mapSettings.lng' },
            { path: 'entity.richContent.nodes.pollData.poll.image.duration' },
            {
              path: 'entity.richContent.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.richContent.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.centerX',
            },
            {
              path: 'entity.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.centerY',
            },
            {
              path: 'entity.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.stops.position',
            },
            {
              path: 'entity.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.centerX',
            },
            {
              path: 'entity.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.centerY',
            },
            {
              path: 'entity.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.stops.position',
            },
            { path: 'entity.richContent.nodes.appEmbedData.image.duration' },
            { path: 'entity.richContent.nodes.videoData.video.duration' },
            { path: 'entity.richContent.nodes.videoData.thumbnail.duration' },
            { path: 'entity.richContent.nodes.audioData.audio.duration' },
            { path: 'entity.richContent.nodes.audioData.coverImage.duration' },
            {
              path: 'entity.richContent.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.richContent.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
            },
            {
              path: 'entity.richContent.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
            },
            {
              path: 'entity.richContent.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
            },
            {
              path: 'entity.richContent.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.richContent.nodes.layoutData.backdropImage.overlay.gradient.centerX',
            },
            {
              path: 'entity.richContent.nodes.layoutData.backdropImage.overlay.gradient.centerY',
            },
            {
              path: 'entity.richContent.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
            },
            {
              path: 'entity.richContent.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.richContent.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.richContent.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.richContent.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.richContent.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.richContent.nodes.layoutData.backdrop.gradient.stops.position',
            },
            { path: 'entity.richContent.nodes.shapeData.shape.duration' },
            {
              path: 'entity.richContent.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.richContent.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.richContent.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.richContent.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.richContent.nodes.cardData.backgroundImage.overlay.gradient.centerX',
            },
            {
              path: 'entity.richContent.nodes.cardData.backgroundImage.overlay.gradient.centerY',
            },
            {
              path: 'entity.richContent.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
            },
            { path: 'entity.richContent.nodes.tocData.fontSize' },
            { path: 'entity.richContent.nodes.tocData.itemSpacing' },
            {
              path: 'entity.richContent.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.centerX',
            },
            {
              path: 'entity.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.centerY',
            },
            {
              path: 'entity.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.stops.position',
            },
            {
              path: 'entity.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.centerX',
            },
            {
              path: 'entity.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.centerY',
            },
            {
              path: 'entity.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.stops.position',
            },
            {
              path: 'entity.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.centerX',
            },
            {
              path: 'entity.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.centerY',
            },
            {
              path: 'entity.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.stops.position',
            },
            {
              path: 'entity.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.centerX',
            },
            {
              path: 'entity.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.centerY',
            },
            {
              path: 'entity.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.stops.position',
            },
            {
              path: 'entity.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.centerX',
            },
            {
              path: 'entity.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.centerY',
            },
            {
              path: 'entity.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.stops.position',
            },
            {
              path: 'entity.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.centerX',
            },
            {
              path: 'entity.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.centerY',
            },
            {
              path: 'entity.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.stops.position',
            },
            {
              path: 'entity.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.centerX',
            },
            {
              path: 'entity.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.centerY',
            },
            {
              path: 'entity.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.stops.position',
            },
            {
              path: 'entity.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.centerX',
            },
            {
              path: 'entity.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.centerY',
            },
            {
              path: 'entity.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.stops.position',
            },
            {
              path: 'entity.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.centerX',
            },
            {
              path: 'entity.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.centerY',
            },
            {
              path: 'entity.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.stops.position',
            },
            {
              path: 'entity.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.centerX',
            },
            {
              path: 'entity.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.centerY',
            },
            {
              path: 'entity.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.stops.position',
            },
            {
              path: 'entity.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.centerX',
            },
            {
              path: 'entity.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.centerY',
            },
            {
              path: 'entity.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.stops.position',
            },
            {
              path: 'entity.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.centerX',
            },
            {
              path: 'entity.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.centerY',
            },
            {
              path: 'entity.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.stops.position',
            },
            {
              path: 'entity.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.centerX',
            },
            {
              path: 'entity.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.centerY',
            },
            {
              path: 'entity.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.stops.position',
            },
            {
              path: 'entity.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.centerX',
            },
            {
              path: 'entity.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.centerY',
            },
            {
              path: 'entity.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.stops.position',
            },
            {
              path: 'entity.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.centerX',
            },
            {
              path: 'entity.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.centerY',
            },
            {
              path: 'entity.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.stops.position',
            },
            {
              path: 'entity.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.centerX',
            },
            {
              path: 'entity.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.centerY',
            },
            {
              path: 'entity.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.stops.position',
            },
            {
              path: 'entity.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.centerX',
            },
            {
              path: 'entity.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.centerY',
            },
            {
              path: 'entity.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.stops.position',
            },
            {
              path: 'entity.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.centerX',
            },
            {
              path: 'entity.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.centerY',
            },
            {
              path: 'entity.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.stops.position',
            },
            { path: 'entity.labels.sortOrder' },
          ],
        },
      ])
    )
)<QuestionEntryCreatedEnvelope>();
export const onQuestionEntryDeleted = /*#__PURE__*/ EventDefinition(
  'wix.faq.question_entry.v2.question_entry_deleted',
  true,
  (event: QuestionEntryDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'undefined.createdDate' },
            { path: 'undefined.updatedDate' },
            { path: 'undefined.richContent.metadata.createdTimestamp' },
            { path: 'undefined.richContent.metadata.updatedTimestamp' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'undefined.sortOrder' },
            {
              path: 'undefined.richContent.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'undefined.richContent.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'undefined.richContent.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'undefined.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'undefined.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'undefined.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'undefined.richContent.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'undefined.richContent.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'undefined.richContent.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'undefined.richContent.nodes.galleryData.options.item.ratio',
            },
            { path: 'undefined.richContent.nodes.imageData.image.duration' },
            { path: 'undefined.richContent.nodes.imageData.crop.x' },
            { path: 'undefined.richContent.nodes.imageData.crop.y' },
            { path: 'undefined.richContent.nodes.imageData.crop.width' },
            { path: 'undefined.richContent.nodes.imageData.crop.height' },
            { path: 'undefined.richContent.nodes.mapData.mapSettings.lat' },
            { path: 'undefined.richContent.nodes.mapData.mapSettings.lng' },
            {
              path: 'undefined.richContent.nodes.pollData.poll.image.duration',
            },
            {
              path: 'undefined.richContent.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'undefined.richContent.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'undefined.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.centerX',
            },
            {
              path: 'undefined.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.centerY',
            },
            {
              path: 'undefined.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.stops.position',
            },
            {
              path: 'undefined.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.centerX',
            },
            {
              path: 'undefined.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.centerY',
            },
            {
              path: 'undefined.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.stops.position',
            },
            { path: 'undefined.richContent.nodes.appEmbedData.image.duration' },
            { path: 'undefined.richContent.nodes.videoData.video.duration' },
            {
              path: 'undefined.richContent.nodes.videoData.thumbnail.duration',
            },
            { path: 'undefined.richContent.nodes.audioData.audio.duration' },
            {
              path: 'undefined.richContent.nodes.audioData.coverImage.duration',
            },
            {
              path: 'undefined.richContent.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'undefined.richContent.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
            },
            {
              path: 'undefined.richContent.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
            },
            {
              path: 'undefined.richContent.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
            },
            {
              path: 'undefined.richContent.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'undefined.richContent.nodes.layoutData.backdropImage.overlay.gradient.centerX',
            },
            {
              path: 'undefined.richContent.nodes.layoutData.backdropImage.overlay.gradient.centerY',
            },
            {
              path: 'undefined.richContent.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
            },
            {
              path: 'undefined.richContent.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'undefined.richContent.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'undefined.richContent.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'undefined.richContent.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'undefined.richContent.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'undefined.richContent.nodes.layoutData.backdrop.gradient.stops.position',
            },
            { path: 'undefined.richContent.nodes.shapeData.shape.duration' },
            {
              path: 'undefined.richContent.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'undefined.richContent.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'undefined.richContent.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'undefined.richContent.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'undefined.richContent.nodes.cardData.backgroundImage.overlay.gradient.centerX',
            },
            {
              path: 'undefined.richContent.nodes.cardData.backgroundImage.overlay.gradient.centerY',
            },
            {
              path: 'undefined.richContent.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
            },
            { path: 'undefined.richContent.nodes.tocData.fontSize' },
            { path: 'undefined.richContent.nodes.tocData.itemSpacing' },
            {
              path: 'undefined.richContent.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'undefined.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.centerX',
            },
            {
              path: 'undefined.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.centerY',
            },
            {
              path: 'undefined.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.stops.position',
            },
            {
              path: 'undefined.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.centerX',
            },
            {
              path: 'undefined.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.centerY',
            },
            {
              path: 'undefined.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.stops.position',
            },
            {
              path: 'undefined.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.centerX',
            },
            {
              path: 'undefined.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.centerY',
            },
            {
              path: 'undefined.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.stops.position',
            },
            {
              path: 'undefined.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.centerX',
            },
            {
              path: 'undefined.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.centerY',
            },
            {
              path: 'undefined.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.stops.position',
            },
            {
              path: 'undefined.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.centerX',
            },
            {
              path: 'undefined.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.centerY',
            },
            {
              path: 'undefined.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.stops.position',
            },
            {
              path: 'undefined.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.centerX',
            },
            {
              path: 'undefined.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.centerY',
            },
            {
              path: 'undefined.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.stops.position',
            },
            {
              path: 'undefined.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.centerX',
            },
            {
              path: 'undefined.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.centerY',
            },
            {
              path: 'undefined.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.stops.position',
            },
            {
              path: 'undefined.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.centerX',
            },
            {
              path: 'undefined.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.centerY',
            },
            {
              path: 'undefined.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.stops.position',
            },
            {
              path: 'undefined.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.centerX',
            },
            {
              path: 'undefined.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.centerY',
            },
            {
              path: 'undefined.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.stops.position',
            },
            {
              path: 'undefined.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.centerX',
            },
            {
              path: 'undefined.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.centerY',
            },
            {
              path: 'undefined.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.stops.position',
            },
            {
              path: 'undefined.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.centerX',
            },
            {
              path: 'undefined.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.centerY',
            },
            {
              path: 'undefined.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.stops.position',
            },
            {
              path: 'undefined.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.centerX',
            },
            {
              path: 'undefined.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.centerY',
            },
            {
              path: 'undefined.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.stops.position',
            },
            {
              path: 'undefined.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.centerX',
            },
            {
              path: 'undefined.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.centerY',
            },
            {
              path: 'undefined.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.stops.position',
            },
            {
              path: 'undefined.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.centerX',
            },
            {
              path: 'undefined.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.centerY',
            },
            {
              path: 'undefined.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.stops.position',
            },
            {
              path: 'undefined.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.centerX',
            },
            {
              path: 'undefined.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.centerY',
            },
            {
              path: 'undefined.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.stops.position',
            },
            {
              path: 'undefined.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.centerX',
            },
            {
              path: 'undefined.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.centerY',
            },
            {
              path: 'undefined.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.stops.position',
            },
            {
              path: 'undefined.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.centerX',
            },
            {
              path: 'undefined.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.centerY',
            },
            {
              path: 'undefined.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.stops.position',
            },
            {
              path: 'undefined.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.centerX',
            },
            {
              path: 'undefined.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.centerY',
            },
            {
              path: 'undefined.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.stops.position',
            },
            { path: 'undefined.labels.sortOrder' },
          ],
        },
      ])
    )
)<QuestionEntryDeletedEnvelope>();
export const onQuestionEntryUpdated = /*#__PURE__*/ EventDefinition(
  'wix.faq.question_entry.v2.question_entry_updated',
  true,
  (event: QuestionEntryUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.richContent.metadata.createdTimestamp' },
            { path: 'entity.richContent.metadata.updatedTimestamp' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'entity.sortOrder' },
            {
              path: 'entity.richContent.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.richContent.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.richContent.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.richContent.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.richContent.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.richContent.nodes.galleryData.items.video.thumbnail.duration',
            },
            { path: 'entity.richContent.nodes.galleryData.options.item.ratio' },
            { path: 'entity.richContent.nodes.imageData.image.duration' },
            { path: 'entity.richContent.nodes.imageData.crop.x' },
            { path: 'entity.richContent.nodes.imageData.crop.y' },
            { path: 'entity.richContent.nodes.imageData.crop.width' },
            { path: 'entity.richContent.nodes.imageData.crop.height' },
            { path: 'entity.richContent.nodes.mapData.mapSettings.lat' },
            { path: 'entity.richContent.nodes.mapData.mapSettings.lng' },
            { path: 'entity.richContent.nodes.pollData.poll.image.duration' },
            {
              path: 'entity.richContent.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.richContent.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.centerX',
            },
            {
              path: 'entity.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.centerY',
            },
            {
              path: 'entity.richContent.nodes.textData.decorations.colorData.foregroundStyle.gradient.stops.position',
            },
            {
              path: 'entity.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.centerX',
            },
            {
              path: 'entity.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.centerY',
            },
            {
              path: 'entity.richContent.nodes.textData.decorations.colorData.backgroundStyle.gradient.stops.position',
            },
            { path: 'entity.richContent.nodes.appEmbedData.image.duration' },
            { path: 'entity.richContent.nodes.videoData.video.duration' },
            { path: 'entity.richContent.nodes.videoData.thumbnail.duration' },
            { path: 'entity.richContent.nodes.audioData.audio.duration' },
            { path: 'entity.richContent.nodes.audioData.coverImage.duration' },
            {
              path: 'entity.richContent.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.richContent.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
            },
            {
              path: 'entity.richContent.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
            },
            {
              path: 'entity.richContent.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
            },
            {
              path: 'entity.richContent.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.richContent.nodes.layoutData.backdropImage.overlay.gradient.centerX',
            },
            {
              path: 'entity.richContent.nodes.layoutData.backdropImage.overlay.gradient.centerY',
            },
            {
              path: 'entity.richContent.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
            },
            {
              path: 'entity.richContent.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.richContent.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.richContent.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.richContent.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.richContent.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.richContent.nodes.layoutData.backdrop.gradient.stops.position',
            },
            { path: 'entity.richContent.nodes.shapeData.shape.duration' },
            {
              path: 'entity.richContent.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.richContent.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.richContent.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.richContent.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.richContent.nodes.cardData.backgroundImage.overlay.gradient.centerX',
            },
            {
              path: 'entity.richContent.nodes.cardData.backgroundImage.overlay.gradient.centerY',
            },
            {
              path: 'entity.richContent.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
            },
            { path: 'entity.richContent.nodes.tocData.fontSize' },
            { path: 'entity.richContent.nodes.tocData.itemSpacing' },
            {
              path: 'entity.richContent.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.centerX',
            },
            {
              path: 'entity.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.centerY',
            },
            {
              path: 'entity.richContent.documentStyle.headerOne.decorations.colorData.foregroundStyle.gradient.stops.position',
            },
            {
              path: 'entity.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.centerX',
            },
            {
              path: 'entity.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.centerY',
            },
            {
              path: 'entity.richContent.documentStyle.headerOne.decorations.colorData.backgroundStyle.gradient.stops.position',
            },
            {
              path: 'entity.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.centerX',
            },
            {
              path: 'entity.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.centerY',
            },
            {
              path: 'entity.richContent.documentStyle.headerTwo.decorations.colorData.foregroundStyle.gradient.stops.position',
            },
            {
              path: 'entity.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.centerX',
            },
            {
              path: 'entity.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.centerY',
            },
            {
              path: 'entity.richContent.documentStyle.headerTwo.decorations.colorData.backgroundStyle.gradient.stops.position',
            },
            {
              path: 'entity.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.centerX',
            },
            {
              path: 'entity.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.centerY',
            },
            {
              path: 'entity.richContent.documentStyle.headerThree.decorations.colorData.foregroundStyle.gradient.stops.position',
            },
            {
              path: 'entity.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.centerX',
            },
            {
              path: 'entity.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.centerY',
            },
            {
              path: 'entity.richContent.documentStyle.headerThree.decorations.colorData.backgroundStyle.gradient.stops.position',
            },
            {
              path: 'entity.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.centerX',
            },
            {
              path: 'entity.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.centerY',
            },
            {
              path: 'entity.richContent.documentStyle.headerFour.decorations.colorData.foregroundStyle.gradient.stops.position',
            },
            {
              path: 'entity.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.centerX',
            },
            {
              path: 'entity.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.centerY',
            },
            {
              path: 'entity.richContent.documentStyle.headerFour.decorations.colorData.backgroundStyle.gradient.stops.position',
            },
            {
              path: 'entity.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.centerX',
            },
            {
              path: 'entity.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.centerY',
            },
            {
              path: 'entity.richContent.documentStyle.headerFive.decorations.colorData.foregroundStyle.gradient.stops.position',
            },
            {
              path: 'entity.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.centerX',
            },
            {
              path: 'entity.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.centerY',
            },
            {
              path: 'entity.richContent.documentStyle.headerFive.decorations.colorData.backgroundStyle.gradient.stops.position',
            },
            {
              path: 'entity.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.centerX',
            },
            {
              path: 'entity.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.centerY',
            },
            {
              path: 'entity.richContent.documentStyle.headerSix.decorations.colorData.foregroundStyle.gradient.stops.position',
            },
            {
              path: 'entity.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.centerX',
            },
            {
              path: 'entity.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.centerY',
            },
            {
              path: 'entity.richContent.documentStyle.headerSix.decorations.colorData.backgroundStyle.gradient.stops.position',
            },
            {
              path: 'entity.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.centerX',
            },
            {
              path: 'entity.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.centerY',
            },
            {
              path: 'entity.richContent.documentStyle.paragraph.decorations.colorData.foregroundStyle.gradient.stops.position',
            },
            {
              path: 'entity.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.centerX',
            },
            {
              path: 'entity.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.centerY',
            },
            {
              path: 'entity.richContent.documentStyle.paragraph.decorations.colorData.backgroundStyle.gradient.stops.position',
            },
            {
              path: 'entity.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.centerX',
            },
            {
              path: 'entity.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.centerY',
            },
            {
              path: 'entity.richContent.documentStyle.blockquote.decorations.colorData.foregroundStyle.gradient.stops.position',
            },
            {
              path: 'entity.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.centerX',
            },
            {
              path: 'entity.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.centerY',
            },
            {
              path: 'entity.richContent.documentStyle.blockquote.decorations.colorData.backgroundStyle.gradient.stops.position',
            },
            {
              path: 'entity.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.centerX',
            },
            {
              path: 'entity.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.centerY',
            },
            {
              path: 'entity.richContent.documentStyle.codeBlock.decorations.colorData.foregroundStyle.gradient.stops.position',
            },
            {
              path: 'entity.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.centerX',
            },
            {
              path: 'entity.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.centerY',
            },
            {
              path: 'entity.richContent.documentStyle.codeBlock.decorations.colorData.backgroundStyle.gradient.stops.position',
            },
            { path: 'entity.labels.sortOrder' },
          ],
        },
      ])
    )
)<QuestionEntryUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  AddressLocation,
  Alignment,
  AnchorData,
  Animation,
  AppEmbedData,
  AppEmbedDataAppDataOneOf,
  AppType,
  ApplicationError,
  AspectRatio,
  Asset,
  AudioData,
  Backdrop,
  BackdropType,
  Background,
  BackgroundGradient,
  BackgroundImage,
  BackgroundType,
  Banner,
  BannerPosition,
  BaseEventMetadata,
  BlockquoteData,
  BookingData,
  Border,
  BorderColors,
  BorderWidths,
  BulkActionMetadata,
  BulkDeleteQuestionEntriesRequest,
  BulkDeleteQuestionEntriesResponse,
  BulkUpdateQuestionEntryOptions,
  BulkUpdateQuestionEntryRequest,
  BulkUpdateQuestionEntryResponse,
  BulkUpdateQuestionEntryResult,
  BulletedListData,
  ButtonData,
  ButtonDataType,
  ButtonStyles,
  CaptionData,
  CardData,
  CardDataBackground,
  CardDataBackgroundType,
  CardStyles,
  CardStylesType,
  CellStyle,
  CheckboxListData,
  CodeBlockData,
  CollapsibleListData,
  ColorData,
  ColorDataBackground,
  ColorDataBackgroundType,
  Colors,
  ColumnSize,
  ContentFormat,
  CreateQuestionEntryRequest,
  CreateQuestionEntryResponse,
  Crop,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  Decoration,
  DecorationDataOneOf,
  DecorationType,
  DeleteContext,
  DeleteQuestionEntryRequest,
  DeleteQuestionEntryResponse,
  DeleteStatus,
  Design,
  DesignTarget,
  Dimensions,
  Direction,
  DividerData,
  DividerDataAlignment,
  DividerDataStyles,
  DocumentStyle,
  DomainEvent,
  DomainEventBodyOneOf,
  EditorlessAssigned,
  EditorlessUnassigned,
  EmbedData,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EntranceAnimation,
  EntranceEffect,
  EntranceEffectType,
  EventData,
  EventMetadata,
  ExtendedFields,
  FieldSet,
  FileData,
  FileSource,
  FileSourceDataOneOf,
  FontFamilyData,
  FontSizeData,
  FontType,
  GIF,
  GIFData,
  GIFType,
  GalleryData,
  GalleryOptions,
  GalleryOptionsLayout,
  GetQuestionEntryRequest,
  GetQuestionEntryResponse,
  Gradient,
  GradientType,
  HTMLData,
  HTMLDataDataOneOf,
  HeadingData,
  Height,
  IdentificationData,
  IdentificationDataIdOneOf,
  Image,
  ImageData,
  ImageDataCrop,
  ImageDataStyles,
  ImagePosition,
  ImagePositionPosition,
  ImageScalingScaling,
  ImageStyles,
  Indentation,
  InitialExpandedItems,
  Item,
  ItemDataOneOf,
  ItemMetadata,
  ItemStyle,
  Label,
  LayerEffect,
  Layers,
  Layout,
  LayoutCellData,
  LayoutData,
  LayoutDataBackground,
  LayoutDataBackgroundImage,
  LayoutDataBackgroundType,
  LayoutType,
  LineCap,
  LineStyle,
  Link,
  LinkData,
  LinkDataOneOf,
  LinkPreviewData,
  LinkPreviewDataStyles,
  ListItemNodeData,
  ListQuestionEntriesOptions,
  ListQuestionEntriesRequest,
  ListQuestionEntriesResponse,
  ListStyle,
  ListValue,
  LoopAnimation,
  LoopEffect,
  LoopEffectType,
  MapData,
  MapSettings,
  MapType,
  MaskedQuestionEntry,
  Media,
  MentionData,
  MessageEnvelope,
  MetaSiteSpecialEvent,
  MetaSiteSpecialEventPayloadOneOf,
  Metadata,
  Namespace,
  NamespaceChanged,
  Node,
  NodeDataOneOf,
  NodeStyle,
  NodeType,
  NullValue,
  OdeditorAssigned,
  OdeditorUnassigned,
  Oembed,
  Option,
  OptionDesign,
  OptionLayout,
  OrderedListData,
  Orientation,
  Origin,
  PDFSettings,
  PagingMetadataV2,
  ParagraphData,
  Permissions,
  PicassoAssigned,
  PicassoUnassigned,
  Placement,
  PlaybackOptions,
  PluginContainerData,
  PluginContainerDataAlignment,
  PluginContainerDataWidth,
  PluginContainerDataWidthDataOneOf,
  PointerEffect,
  PointerEffectType,
  Poll,
  PollData,
  PollDataLayout,
  PollDesign,
  PollDesignBackground,
  PollDesignBackgroundBackgroundOneOf,
  PollDesignBackgroundType,
  PollLayout,
  PollLayoutDirection,
  PollLayoutType,
  Position,
  PricingData,
  QueryQuestionEntriesOptions,
  QueryQuestionEntriesRequest,
  QueryQuestionEntriesRequestContentFormat,
  QueryQuestionEntriesRequestFieldSet,
  QueryQuestionEntriesResponse,
  QuestionEntriesQueryBuilder,
  QuestionEntriesQueryResult,
  QuestionEntry,
  QuestionEntryAnswerOneOf,
  QuestionEntryCreatedEnvelope,
  QuestionEntryDeletedEnvelope,
  QuestionEntryQuerySpec,
  QuestionEntryUpdatedEnvelope,
  QuestionStatus,
  Rel,
  Resizing,
  ResponsivenessBehaviour,
  RestoreInfo,
  RibbonStyles,
  RichContent,
  RunInstallationContentRequest,
  RunInstallationContentResponse,
  Scaling,
  SectionDivider,
  ServiceProvisioned,
  ServiceRemoved,
  SetQuestionEntryLabelsOptions,
  SetQuestionEntryLabelsRequest,
  SetQuestionEntryLabelsResponse,
  Settings,
  Shape,
  ShapeData,
  ShapeDataStyles,
  SiteCreated,
  SiteCreatedContext,
  SiteDeleted,
  SiteHardDeleted,
  SiteMarkedAsTemplate,
  SiteMarkedAsWixSite,
  SitePublished,
  SitePurgedExternally,
  SiteRenamed,
  SiteTransferred,
  SiteUndeleted,
  SiteUnpublished,
  SiteUrlChanged,
  SketchData,
  SlotData,
  SlotDesign,
  SmartBlockCellData,
  SmartBlockData,
  SmartBlockDataType,
  SortOrder,
  Sorting,
  Source,
  Spoiler,
  SpoilerData,
  State,
  Stop,
  StudioAssigned,
  StudioTwoAssigned,
  StudioTwoUnassigned,
  StudioUnassigned,
  Styles,
  StylesBorder,
  StylesPosition,
  TableCellData,
  TableData,
  Target,
  TextAlignment,
  TextData,
  TextNodeStyle,
  TextStyle,
  Thumbnails,
  ThumbnailsAlignment,
  TocData,
  Type,
  UpdateExtendedFieldsOptions,
  UpdateExtendedFieldsRequest,
  UpdateExtendedFieldsResponse,
  UpdateQuestionEntry,
  UpdateQuestionEntryRequest,
  UpdateQuestionEntryResponse,
  UserDomainMediaDisabled,
  UserDomainMediaEnabled,
  Variant,
  VerticalAlignment,
  VerticalAlignmentAlignment,
  Video,
  VideoData,
  ViewMode,
  ViewRole,
  VoteRole,
  WebhookIdentityType,
  Width,
  WidthType,
  WixelAssigned,
  WixelUnassigned,
  utils,
} from './faq-question-entry-v2-question-entry-question-entry.universal.js';
